import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

// Learning Module related types
export interface LearningModule {
  module_id: string;
  url_alias: string;
  title: string;
  description?: string;
  status: 'available' | 'in_progress' | 'completed' | 'draft';
  category: string;
  created_at?: string;
  updated_at?: string;
}

export interface ModuleSubscription {
  subscription_id: string;
  user_id: number;
  module_id: string;
  status: 'subscribed' | 'unsubscribed' | 'completed';
  subscribed_at: string;
  unsubscribed_at?: string | null;
  completed_at?: string | null;
  last_accessed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubscribedModule extends LearningModule {
  subscription_id: string;
  subscription_status: 'subscribed' | 'unsubscribed' | 'completed';
  subscribed_at: string;
  unsubscribed_at?: string | null;
  completed_at?: string | null;
  last_accessed_at?: string | null;
}

export interface SubscriptionStats {
  total_subscriptions: number;
  active_subscriptions: number;
  completed_modules: number;
  unsubscribed_count: number;
}

export interface CreateLearningModuleData {
  url_alias: string;
  title: string;
  description?: string;
  status?: 'available' | 'in_progress' | 'completed' | 'draft';
  category: string;
}

export interface UpdateLearningModuleData {
  module_id: string;
  url_alias?: string;
  title?: string;
  description?: string;
  status?: 'available' | 'in_progress' | 'completed' | 'draft';
  category?: string;
}

export interface LearningModuleSearchParams {
  search_term?: string;
  category?: string;
  status?: 'available' | 'in_progress' | 'completed' | 'draft';
  limit?: number;
  offset?: number;
  includeDraft?: boolean;
}

export interface LearningModuleCategory {
  category: string;
  module_count: number;
}

class LearningModuleService {
  // ================================================
  // LEARNING MODULE MANAGEMENT
  // ================================================

  // Create a new learning module
  async createLearningModule(moduleData: CreateLearningModuleData): Promise<{ success: boolean; module_id?: string; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/learning-modules', {
        method: 'POST',
        body: JSON.stringify(moduleData)
      });
      
      return response;
    } catch (error) {
      console.error('Error creating learning module:', error);
      throw error;
    }
  }

  // Update an existing learning module
  async updateLearningModule(data: UpdateLearningModuleData): Promise<{ success: boolean; message?: string }> {
    try {
      const { module_id, ...updateData } = data;
      
      const response = await makeAuthenticatedRequest(`/learning-modules/${module_id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating learning module:', error);
      throw error;
    }
  }

  // Delete a learning module
  async deleteLearningModule(moduleId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-modules/${moduleId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting learning module:', error);
      throw error;
    }
  }

  // Get learning module by ID
  async getLearningModuleById(moduleId: string): Promise<LearningModule | null> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-modules/${moduleId}`, {
        method: 'GET'
      });
      
      if (response.success && response.module) {
        return response.module as LearningModule;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting learning module by ID:', error);
      return null;
    }
  }

  // Get learning module by URL alias
  async getLearningModuleByAlias(alias: string): Promise<LearningModule | null> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-modules/alias/${encodeURIComponent(alias)}`, {
        method: 'GET'
      });
      
      if (response.success && response.module) {
        return response.module as LearningModule;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting learning module by alias:', error);
      return null;
    }
  }

  // Get all learning modules with filtering
  async getAllLearningModules(filters?: {
    status?: 'available' | 'in_progress' | 'completed' | 'draft';
    category?: string;
    limit?: number;
    offset?: number;
    includeDraft?: boolean;
  }): Promise<LearningModule[]> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.category) queryParams.append('category', filters.category);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      // Hide draft modules by default unless explicitly requested
      if (filters?.includeDraft !== undefined) queryParams.append('includeDraft', filters.includeDraft.toString());
      
      const queryString = queryParams.toString();
      const url = `/learning-modules${queryString ? `?${queryString}` : ''}`;
      
      console.log('🔍 LearningModuleService::getAllLearningModules::Making request to:', url, 'with filters:', filters);
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      console.log('🔍 LearningModuleService::getAllLearningModules::Response:', response);
      
      if (response.success && response.modules) {
        console.log('🔍 LearningModuleService::getAllLearningModules::Retrieved modules:', response.modules.length);
        return response.modules as LearningModule[];
      }
      
      console.warn('🔍 LearningModuleService::getAllLearningModules::No modules found or request failed');
      return [];
    } catch (error) {
      console.error('🔍 LearningModuleService::getAllLearningModules::Error getting all learning modules:', error);
      return [];
    }
  }

  // Search learning modules
  async searchLearningModules(searchParams?: LearningModuleSearchParams): Promise<LearningModule[]> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (searchParams?.search_term) queryParams.append('search_term', searchParams.search_term);
      if (searchParams?.category) queryParams.append('category', searchParams.category);
      if (searchParams?.status) queryParams.append('status', searchParams.status);
      if (searchParams?.limit) queryParams.append('limit', searchParams.limit.toString());
      if (searchParams?.offset) queryParams.append('offset', searchParams.offset.toString());
      // Hide draft modules by default unless explicitly requested
      if (searchParams?.includeDraft !== undefined) queryParams.append('includeDraft', searchParams.includeDraft.toString());
      
      const queryString = queryParams.toString();
      const url = `/learning-modules/search${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      if (response.success && response.modules) {
        return response.modules as LearningModule[];
      }
      
      return [];
    } catch (error) {
      console.error('Error searching learning modules:', error);
      return [];
    }
  }

  // Get learning modules by category
  async getLearningModulesByCategory(category: string, filters?: {
    status?: 'available' | 'in_progress' | 'completed' | 'draft';
    limit?: number;
    offset?: number;
    includeDraft?: boolean;
  }): Promise<LearningModule[]> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      // Hide draft modules by default unless explicitly requested
      if (filters?.includeDraft !== undefined) queryParams.append('includeDraft', filters.includeDraft.toString());
      
      const queryString = queryParams.toString();
      const url = `/learning-modules/category/${encodeURIComponent(category)}${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      if (response.success && response.modules) {
        return response.modules as LearningModule[];
      }
      
      return [];
    } catch (error) {
      console.error('Error getting learning modules by category:', error);
      return [];
    }
  }

  // Get all available categories
  async getCategories(): Promise<LearningModuleCategory[]> {
    try {
      const response = await makeAuthenticatedRequest('/learning-modules/categories', {
        method: 'GET'
      });
      
      if (response.success && response.categories) {
        return response.categories as LearningModuleCategory[];
      }
      
      return [];
    } catch (error) {
      console.error('Error getting learning module categories:', error);
      return [];
    }
  }

  // ================================================
  // LEARNING MODULE STATUS MANAGEMENT
  // ================================================

  // Update learning module status
  async updateModuleStatus(moduleId: string, status: 'available' | 'in_progress' | 'completed' | 'draft'): Promise<{ success: boolean; message?: string }> {
    try {
      return await this.updateLearningModule({
        module_id: moduleId,
        status: status
      });
    } catch (error) {
      console.error('Error updating module status:', error);
      throw error;
    }
  }

  // Mark module as in progress
  async markModuleInProgress(moduleId: string): Promise<{ success: boolean; message?: string }> {
    return this.updateModuleStatus(moduleId, 'in_progress');
  }

  // Mark module as completed
  async markModuleCompleted(moduleId: string): Promise<{ success: boolean; message?: string }> {
    return this.updateModuleStatus(moduleId, 'completed');
  }

  // Mark module as available
  async markModuleAvailable(moduleId: string): Promise<{ success: boolean; message?: string }> {
    return this.updateModuleStatus(moduleId, 'available');
  }

  // Mark module as draft
  async markModuleAsDraft(moduleId: string): Promise<{ success: boolean; message?: string }> {
    return this.updateModuleStatus(moduleId, 'draft');
  }

  // ================================================
  // UTILITY FUNCTIONS
  // ================================================

  // Generate a URL alias from title
  generateUrlAlias(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50);
  }

  // Get modules by status
  async getModulesByStatus(status: 'available' | 'in_progress' | 'completed' | 'draft'): Promise<LearningModule[]> {
    return this.getAllLearningModules({ status });
  }

  // Get available modules
  async getAvailableModules(): Promise<LearningModule[]> {
    return this.getModulesByStatus('available');
  }

  // Get modules in progress
  async getModulesInProgress(): Promise<LearningModule[]> {
    try {
      console.log('🔍 LearningModuleService::getModulesInProgress::Starting to fetch in-progress modules');
      const modules = await this.getModulesByStatus('in_progress');
      console.log('🔍 LearningModuleService::getModulesInProgress::Retrieved modules:', modules.length, modules);
      return modules;
    } catch (error) {
      console.error('🔍 LearningModuleService::getModulesInProgress::Error:', error);
      return [];
    }
  }

  // Get completed modules
  async getCompletedModules(): Promise<LearningModule[]> {
    return this.getModulesByStatus('completed');
  }

  // Get draft modules
  async getDraftModules(): Promise<LearningModule[]> {
    return this.getModulesByStatus('draft');
  }

  // Check if module exists by alias
  async moduleExistsByAlias(alias: string): Promise<boolean> {
    try {
      const module = await this.getLearningModuleByAlias(alias);
      return module !== null;
    } catch (error) {
      console.error('Error checking if module exists by alias:', error);
      return false;
    }
  }

  // Check if module exists by ID
  async moduleExistsById(moduleId: string): Promise<boolean> {
    try {
      const module = await this.getLearningModuleById(moduleId);
      return module !== null;
    } catch (error) {
      console.error('Error checking if module exists by ID:', error);
      return false;
    }
  }

  // Get module count by category
  async getModuleCountByCategory(category: string): Promise<number> {
    try {
      const modules = await this.getLearningModulesByCategory(category);
      return modules.length;
    } catch (error) {
      console.error('Error getting module count by category:', error);
      return 0;
    }
  }

  // Get total module count
  async getTotalModuleCount(): Promise<number> {
    try {
      const modules = await this.getAllLearningModules();
      return modules.length;
    } catch (error) {
      console.error('Error getting total module count:', error);
      return 0;
    }
  }

  // Convert API response to LearningModule interface (if needed for compatibility)
  convertApiModule(apiModule: any): LearningModule {
    return {
      module_id: apiModule.module_id,
      url_alias: apiModule.url_alias,
      title: apiModule.title,
      description: apiModule.description,
      status: apiModule.status,
      category: apiModule.category,
      created_at: apiModule.created_at,
      updated_at: apiModule.updated_at
    };
  }

  // Validate learning module data
  validateModuleData(moduleData: Partial<CreateLearningModuleData>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!moduleData.url_alias || moduleData.url_alias.trim() === '') {
      errors.push('URL alias is required');
    }

    if (!moduleData.title || moduleData.title.trim() === '') {
      errors.push('Title is required');
    }

    if (!moduleData.category || moduleData.category.trim() === '') {
      errors.push('Category is required');
    }

    if (moduleData.status && !['available', 'in_progress', 'completed', 'draft'].includes(moduleData.status)) {
      errors.push('Status must be one of: available, in_progress, completed, draft');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Format module for display
  formatModuleForDisplay(module: LearningModule): {
    id: string;
    alias: string;
    title: string;
    description: string;
    status: string;
    statusLabel: string;
    category: string;
    createdAt: string;
    updatedAt: string;
  } {
    const statusLabels = {
      available: 'Available',
      in_progress: 'In Progress',
      completed: 'Completed',
      draft: 'Draft'
    };

    return {
      id: module.module_id,
      alias: module.url_alias,
      title: module.title,
      description: module.description || 'No description available',
      status: module.status,
      statusLabel: statusLabels[module.status] || module.status,
      category: module.category,
      createdAt: module.created_at || '',
      updatedAt: module.updated_at || ''
    };
  }

  // Group modules by category
  groupModulesByCategory(modules: LearningModule[]): { [category: string]: LearningModule[] } {
    return modules.reduce((grouped, module) => {
      const category = module.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(module);
      return grouped;
    }, {} as { [category: string]: LearningModule[] });
  }

  // Group modules by status
  groupModulesByStatus(modules: LearningModule[]): { 
    available: LearningModule[];
    in_progress: LearningModule[];
    completed: LearningModule[];
    draft: LearningModule[];
  } {
    return modules.reduce((grouped, module) => {
      grouped[module.status].push(module);
      return grouped;
    }, {
      available: [] as LearningModule[],
      in_progress: [] as LearningModule[],
      completed: [] as LearningModule[],
      draft: [] as LearningModule[]
    });
  }

  // Sort modules by various criteria
  sortModules(modules: LearningModule[], sortBy: 'title' | 'category' | 'status' | 'created_at' | 'updated_at', order: 'asc' | 'desc' = 'asc'): LearningModule[] {
    return [...modules].sort((a, b) => {
      let aValue: string | Date;
      let bValue: string | Date;

      switch (sortBy) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'category':
          aValue = a.category.toLowerCase();
          bValue = b.category.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'created_at':
          aValue = new Date(a.created_at || '');
          bValue = new Date(b.created_at || '');
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at || '');
          bValue = new Date(b.updated_at || '');
          break;
        default:
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  // ================================================
  // SUBSCRIPTION MANAGEMENT
  // ================================================

  // Subscribe to a learning module
  async subscribeToModule(moduleId: string): Promise<{ success: boolean; subscription_id?: string; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-modules/${moduleId}/subscribe`, {
        method: 'POST'
      });
      
      return response;
    } catch (error) {
      console.error('Error subscribing to module:', error);
      throw error;
    }
  }

  // Unsubscribe from a learning module
  async unsubscribeFromModule(moduleId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-modules/${moduleId}/unsubscribe`, {
        method: 'POST'
      });
      
      return response;
    } catch (error) {
      console.error('Error unsubscribing from module:', error);
      throw error;
    }
  }

  // Mark a module as completed
  async completeModule(moduleId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-modules/${moduleId}/complete`, {
        method: 'POST'
      });
      
      return response;
    } catch (error) {
      console.error('Error marking module as completed:', error);
      throw error;
    }
  }

  // Update last accessed time for a module
  async updateLastAccessed(moduleId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-modules/${moduleId}/access`, {
        method: 'POST'
      });
      
      return response;
    } catch (error) {
      console.error('Error updating last accessed time:', error);
      throw error;
    }
  }

  // Get user's subscribed modules
  async getMySubscriptions(filters?: {
    status?: 'subscribed' | 'unsubscribed' | 'completed';
    limit?: number;
    offset?: number;
  }): Promise<SubscribedModule[]> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/learning-modules/subscriptions/my${queryString ? `?${queryString}` : ''}`;
      
      console.log('🔍 LearningModuleService::getMySubscriptions::Making request to:', url);
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      console.log('🔍 LearningModuleService::getMySubscriptions::Response:', response);
      
      if (response.success && response.subscriptions) {
        console.log('🔍 LearningModuleService::getMySubscriptions::Retrieved subscriptions:', response.subscriptions.length);
        return response.subscriptions as SubscribedModule[];
      }
      
      console.warn('🔍 LearningModuleService::getMySubscriptions::No subscriptions found or request failed');
      return [];
    } catch (error) {
      console.error('🔍 LearningModuleService::getMySubscriptions::Error getting subscriptions:', error);
      return [];
    }
  }

  // Check if user is subscribed to a module
  async checkSubscription(moduleId: string): Promise<{
    subscribed: boolean;
    subscription: ModuleSubscription | null;
  }> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-modules/${moduleId}/subscription`, {
        method: 'GET'
      });
      
      if (response.success) {
        return {
          subscribed: response.subscribed || false,
          subscription: response.subscription as ModuleSubscription | null
        };
      }
      
      return {
        subscribed: false,
        subscription: null
      };
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return {
        subscribed: false,
        subscription: null
      };
    }
  }

  // Get subscription statistics
  async getSubscriptionStats(): Promise<SubscriptionStats | null> {
    try {
      const response = await makeAuthenticatedRequest('/learning-modules/subscriptions/stats', {
        method: 'GET'
      });
      
      if (response.success && response.stats) {
        return response.stats as SubscriptionStats;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting subscription statistics:', error);
      return null;
    }
  }

  // Get active subscriptions
  async getActiveSubscriptions(): Promise<SubscribedModule[]> {
    return this.getMySubscriptions({ status: 'subscribed' });
  }

  // Get completed subscriptions
  async getCompletedSubscriptions(): Promise<SubscribedModule[]> {
    return this.getMySubscriptions({ status: 'completed' });
  }

  // Get all subscriptions (regardless of status)
  async getAllSubscriptions(): Promise<SubscribedModule[]> {
    return this.getMySubscriptions();
  }

  // Check if user is actively subscribed to a module
  async isSubscribed(moduleId: string): Promise<boolean> {
    try {
      const { subscribed } = await this.checkSubscription(moduleId);
      return subscribed;
    } catch (error) {
      console.error('Error checking if subscribed:', error);
      return false;
    }
  }

  // Toggle subscription (subscribe if not subscribed, unsubscribe if subscribed)
  async toggleSubscription(moduleId: string): Promise<{ success: boolean; message?: string; action: 'subscribed' | 'unsubscribed' }> {
    try {
      const { subscribed } = await this.checkSubscription(moduleId);
      
      if (subscribed) {
        const result = await this.unsubscribeFromModule(moduleId);
        return {
          ...result,
          action: 'unsubscribed'
        };
      } else {
        const result = await this.subscribeToModule(moduleId);
        return {
          ...result,
          action: 'subscribed'
        };
      }
    } catch (error) {
      console.error('Error toggling subscription:', error);
      throw error;
    }
  }

  // Format subscription for display
  formatSubscriptionForDisplay(subscription: SubscribedModule): {
    id: string;
    moduleId: string;
    title: string;
    description: string;
    category: string;
    subscriptionStatus: string;
    subscriptionStatusLabel: string;
    subscribedAt: string;
    lastAccessedAt: string;
    completedAt: string;
    isActive: boolean;
    isCompleted: boolean;
  } {
    const statusLabels = {
      subscribed: 'Active',
      unsubscribed: 'Unsubscribed',
      completed: 'Completed'
    };

    return {
      id: subscription.subscription_id,
      moduleId: subscription.module_id,
      title: subscription.title,
      description: subscription.description || 'No description available',
      category: subscription.category,
      subscriptionStatus: subscription.subscription_status,
      subscriptionStatusLabel: statusLabels[subscription.subscription_status] || subscription.subscription_status,
      subscribedAt: subscription.subscribed_at || '',
      lastAccessedAt: subscription.last_accessed_at || 'Never',
      completedAt: subscription.completed_at || 'Not completed',
      isActive: subscription.subscription_status === 'subscribed',
      isCompleted: subscription.subscription_status === 'completed'
    };
  }

  // Group subscriptions by status
  groupSubscriptionsByStatus(subscriptions: SubscribedModule[]): {
    subscribed: SubscribedModule[];
    unsubscribed: SubscribedModule[];
    completed: SubscribedModule[];
  } {
    return subscriptions.reduce((grouped, subscription) => {
      grouped[subscription.subscription_status].push(subscription);
      return grouped;
    }, {
      subscribed: [] as SubscribedModule[],
      unsubscribed: [] as SubscribedModule[],
      completed: [] as SubscribedModule[]
    });
  }

  // Group subscriptions by category
  groupSubscriptionsByCategory(subscriptions: SubscribedModule[]): { [category: string]: SubscribedModule[] } {
    return subscriptions.reduce((grouped, subscription) => {
      const category = subscription.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(subscription);
      return grouped;
    }, {} as { [category: string]: SubscribedModule[] });
  }

  // Sort subscriptions by last accessed
  sortSubscriptionsByLastAccessed(subscriptions: SubscribedModule[], order: 'asc' | 'desc' = 'desc'): SubscribedModule[] {
    return [...subscriptions].sort((a, b) => {
      const aDate = new Date(a.last_accessed_at || 0);
      const bDate = new Date(b.last_accessed_at || 0);
      
      if (aDate < bDate) {
        return order === 'asc' ? -1 : 1;
      }
      if (aDate > bDate) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }
}

export const learningModuleService = new LearningModuleService();
